import { useState } from 'react'
import { motion } from 'framer-motion'
import { FaCheck, FaTimes } from 'react-icons/fa'

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly') // monthly or annual

  const plans = [
    {
      name: 'Sprout',
      tagline: 'Perfect for solo practitioners',
      monthlyPrice: 39,
      annualPrice: 390, // 2 months free
      features: [
        { name: 'Up to 30 clients', included: true },
        { name: '1 practitioner', included: true },
        { name: 'Client management', included: true },
        { name: 'Session notes', included: true },
        { name: 'Basic invoicing', included: true },
        { name: '1GB document storage', included: true },
        { name: 'Email support', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Appointment scheduling', included: false },
        { name: 'Calendar integration', included: false },
        { name: 'Automated reminders', included: false },
        { name: 'Custom branding', included: false },
        { name: 'API access', included: false },
        { name: 'Priority support', included: false }
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Flourish',
      tagline: 'For growing practices',
      monthlyPrice: 79,
      annualPrice: 790, // 2 months free
      features: [
        { name: 'Up to 100 clients', included: true },
        { name: 'Up to 3 practitioners', included: true },
        { name: 'Client management', included: true },
        { name: 'Session notes', included: true },
        { name: 'Advanced invoicing', included: true },
        { name: '5GB document storage', included: true },
        { name: 'Priority email support', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Appointment scheduling', included: true },
        { name: 'Calendar integration', included: true },
        { name: 'Automated reminders', included: true },
        { name: 'Custom branding', included: false },
        { name: 'API access', included: false },
        { name: '24/7 priority support', included: false }
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Sanctuary',
      tagline: 'For established practices',
      monthlyPrice: 149,
      annualPrice: 1490, // 2 months free
      features: [
        { name: 'Unlimited clients', included: true },
        { name: 'Unlimited practitioners', included: true },
        { name: 'Client management', included: true },
        { name: 'Session notes', included: true },
        { name: 'Advanced invoicing', included: true },
        { name: 'Unlimited document storage', included: true },
        { name: '24/7 priority support', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Appointment scheduling', included: true },
        { name: 'Calendar integration', included: true },
        { name: 'Automated reminders', included: true },
        { name: 'Custom branding', included: true },
        { name: 'API access', included: true },
        { name: 'Dedicated account manager', included: true }
      ],
      cta: 'Start Free Trial',
      popular: false
    }
  ]

  const faqs = [
    {
      question: 'Can I change plans later?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate your billing accordingly.'
    },
    {
      question: 'What happens after the free trial?',
      answer: 'After your 30-day free trial, you\'ll be automatically enrolled in the plan you selected. You can cancel anytime during or after the trial with no charges.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use enterprise-grade encryption, comply with HIPAA regulations, and maintain SOC 2 Type II certification. Your data is backed up daily and stored in secure data centers.'
    },
    {
      question: 'Can I import my existing client data?',
      answer: 'Yes! We provide CSV import tools to help you migrate your existing client data. Our support team can assist with the migration process.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure Stripe payment processor.'
    }
  ]

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-600 via-primary-700 to-primary-900 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-20"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg md:text-xl text-primary-100 max-w-3xl mx-auto mb-8 leading-relaxed">
              Choose the plan that fits your practice. All plans include a 30-day free trial.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  billingCycle === 'annual'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual
                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular
                    ? 'bg-gradient-to-br from-primary-600 to-accent-600 text-white shadow-2xl scale-105'
                    : 'bg-gray-50 text-gray-900 shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold mb-2">{plan.name}</h3>
                  <p className={`text-sm ${plan.popular ? 'text-primary-100' : 'text-gray-600'}`}>
                    {plan.tagline}
                  </p>
                </div>

                <div className="text-center mb-8">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold">
                      ${getPrice(plan)}
                    </span>
                    <span className={`ml-2 ${plan.popular ? 'text-primary-100' : 'text-gray-600'}`}>
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className={`text-sm mt-2 ${plan.popular ? 'text-primary-100' : 'text-gray-600'}`}>
                      ${(getPrice(plan) / 12).toFixed(2)}/month billed annually
                    </p>
                  )}
                </div>

                <a
                  href={`https://acuitypractice.com/signup?plan=${plan.name.toLowerCase()}&trial=true`}
                  className={`block w-full text-center py-3 rounded-lg font-semibold mb-8 transition-all ${
                    plan.popular
                      ? 'bg-white text-primary-600 hover:bg-gray-100'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {plan.cta}
                </a>

                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center">
                      {feature.included ? (
                        <FaCheck className={`w-5 h-5 mr-3 flex-shrink-0 ${
                          plan.popular ? 'text-white' : 'text-green-600'
                        }`} />
                      ) : (
                        <FaTimes className={`w-5 h-5 mr-3 flex-shrink-0 ${
                          plan.popular ? 'text-primary-200' : 'text-gray-400'
                        }`} />
                      )}
                      <span className={`text-sm ${
                        feature.included
                          ? plan.popular ? 'text-white' : 'text-gray-900'
                          : plan.popular ? 'text-primary-200' : 'text-gray-500'
                      }`}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-gray-600 mb-4">
              All plans include a 30-day free trial • No credit card required • Cancel anytime
            </p>
            <p className="text-sm text-gray-500">
              Need a custom enterprise plan? <a href="/contact" className="text-primary-600 hover:underline">Contact us</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-10"></div>

        <div className="container-custom max-w-4xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-300">
              Got questions? We've got answers.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-lg p-6 border border-gray-600 hover:border-primary-500 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/20"
              >
                <h3 className="text-xl font-semibold text-white mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-gray-300">
              Still have questions?{' '}
              <a href="/contact" className="text-accent-400 hover:text-accent-300 hover:underline font-semibold transition-colors">
                Contact our team
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-15"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-15"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-12 md:p-16 shadow-2xl max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Start Your Free Trial Today
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Join thousands of practitioners who have transformed their practice with Acuity Practice.
            </p>
            <a href="https://acuitypractice.com/signup?plan=flourish&trial=true" className="btn-primary text-lg">
              Get Started Free
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default PricingPage
